<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { getSessionInfo, trackEvent, trackMatchShare } from '../lib/apiService';
  import type { SessionInfo, Award} from '../types';
  import { formatMatchDate, getWeatherEmoji, roundToNearestHour, getTeamData, loadImagePromise, wrapText, getXforRect, getTaskHeight, isShareAvailable } from '../lib/utils/shareUtils';
  import { drawAutoSizedText, drawRoundedRect, drawLogoScaled, drawGradientOverlay, drawPanklaWatermark, getPlayerStats, drawPlayerStats, extractDominantColor } from '../lib/utils/canvasUtils';
  import { getAwards } from '../lib/utils/reportUtils';
  import { createEventDispatcher } from 'svelte';
  export let report = null;
  export let reportImage = null;
  export let reportImages = null;
  export let cardType: 'match_announcement' | 'match_report' | 'player_award' = 'match_report';
  export let selectedAward: Award | null = null;
  export let layout: 'portrait' | 'landscape' = 'portrait'; // Layout selection for match_report
  
  let canvas;
  let canvasFullscreen;
  let ctx;
  let isEditorOpen = false;
  let scale = 1;
  let offsetX = 0;
  let offsetY = 0;
  let isDragging = false;
  let lastX = 0;
  let lastY = 0;
  let frameWidth = cardType === 'player_award' ? 800 : 900;
  let frameHeight = cardType === 'player_award' ? 800 : 1600;
  let fontLoaded = false;
  let imagesLoaded = false;
  let preloadedImages = {
    background: null,
    homeLogo: null,
    awayLogo: null,
    teamLogo: null,
    backgrounds: {},
    dominantColor: null
  };
  
  // Award icon position for player award card
  let awards: Award[] = [];
  let awardIconX = 200;
  let awardIconY = 250;
  let awardIconSize = 80; // Control icon size
  
  // Define the editing mode (background or icon)
  let moveMode: 'background' | 'icon' = 'background';
  
  // Add build timestamp for cache busting
  // @ts-ignore - Vite-specific type that TypeScript doesn't know about
  const BUILD_TIMESTAMP = import.meta.env.VITE_BUILD_TIMESTAMP || Date.now();
  
  let sessionInfo: SessionInfo | null = null;
  
  // Add weatherData variable to store the fetched data
  let weatherData = null;
  let weatherPromise = null;
  
  // Add a new variable to track icon visibility
  let isIconVisible = false;

  // Check if selected award has a player assigned
  $: hasPlayerForAward = selectedAward && report?.player_awards?.find(award => award.id === selectedAward.id)?.name;
  
  // Variables for the resize button
  let showResizeButton = false;
  let resizeButtonTimer = null;
  
  // Functions to handle resize button visibility
  function startResizeButtonTimer() {
    // Clear any existing timer
    if (resizeButtonTimer) {
      clearTimeout(resizeButtonTimer);
    }
    
    // Show the button, small delay to be more in line with the canvas drawing
    setTimeout(() => {
      showResizeButton = true;
    }, 100);

    // Set timer to hide after 5 seconds
    resizeButtonTimer = setTimeout(() => {
      showResizeButton = false;
    }, 5000);
  }
  
  function hideResizeButton() {
    showResizeButton = false;
    if (resizeButtonTimer) {
      clearTimeout(resizeButtonTimer);
      resizeButtonTimer = null;
    }
  }

  // Function to load weather data
  async function loadWeatherData() {
    if (!report || !report.opponent_club) return null;
    
    try {
      // @ts-ignore - Vite-specific type that TypeScript doesn't know about
      const weatherApiKey = import.meta.env.VITE_WEATHER_API_KEY;
      const weatherResponse = await fetch(`https://api.weatherapi.com/v1/forecast.json?key=${weatherApiKey}&q=${report.opponent_club?.location_name},${report.opponent_club?.location_address},${report.opponent_club?.location_postal_code},${report.opponent_club?.location_city},${report.opponent_club?.location_country}&days=14&aqi=no&alerts=no`);
      return await weatherResponse.json();
    } catch (error) {
      console.error('Error fetching weather data:', error);
      return null;
    }
  }
  
  // Function to get the card id based on card type
  function getCardId() {
    if (cardType === 'match_announcement') return 'announcement';
    if (cardType === 'match_report') return 'match_report';
    return 'player_awards';
  }
  
  // Function to get card version id
  function getCardVersionId() {
    if (cardType === 'player_award' && selectedAward) {
      return selectedAward.id;
    }
    // For match reports, return different IDs based on layout
    if (cardType === 'match_report') {
      return `match_report_${layout}`;
    }
    return getCardId();
  }


  // Function to load image settings from report.image_data
  function loadImageSettings() {
    if (!report || !report.image_data) return;

    const cardId = getCardId();
    const cardVersionId = getCardVersionId();

    // Reset to defaults first (in case no saved settings exist)
    offsetX = 0;
    offsetY = 0;
    scale = 1;

    // Get the card versions for this card type
    const cardVersions = report.image_data[cardId];
    if (!cardVersions) {
      if (canvas) drawFrame();
      return;
    }

    // Find the specific version for this card
    const cardVersion = cardVersions.find(v => v.id === cardVersionId);
    if (!cardVersion) {
      if (canvas) drawFrame();
      return;
    }

    // Determine icon visibility for player award based on presence of icon data
    if (cardType === 'player_award') {
      // Check if there's an icon image in the card version
      const hasIconImage = cardVersion.images.some(img => img.id === 'icon');

      // Set visibility based on icon presence and explicit visibility flag
      isIconVisible = hasIconImage;

      // Ensure moveMode is set to background if icon is hidden
      if (!isIconVisible) {
        moveMode = 'background';
      }
    }

    // Load settings for each image
    cardVersion.images.forEach(image => {
      if (image.id === 'background') {
        offsetX = image.setup.posX;
        offsetY = image.setup.posY;
        scale = image.setup.scale;
      } else if (image.id === 'icon' && cardType === 'player_award') {
        awardIconX = image.setup.posX;
        awardIconY = image.setup.posY;
        awardIconSize = image.setup.scale;
      }
    });

    // Redraw with loaded settings
    if (canvas) {
      drawFrame();
    }
  }
  
  // Function to save current image settings to report.image_data
  function saveImageSettings() {

    if (!report) return;
    
    // Initialize image_data if it doesn't exist
    if (!report.image_data) {
      report.image_data = {};
    }
    
    const cardId = getCardId();
    const cardVersionId = getCardVersionId();
    
    // Initialize card versions array if it doesn't exist
    if (!report.image_data[cardId]) {
      report.image_data[cardId] = [];
    }
    
    // Find existing card version or create new one
    let cardVersion = report.image_data[cardId].find(v => v.id === cardVersionId);
    
    if (!cardVersion) {
      cardVersion = {
        id: cardVersionId,
        images: []
      };
      report.image_data[cardId].push(cardVersion);
    }
    
    // Update or add background image settings
    let backgroundImage = cardVersion.images.find(img => img.id === 'background');
    if (backgroundImage) {
      backgroundImage.setup = { posX: offsetX, posY: offsetY, scale: scale };
    } else {
      cardVersion.images.push({
        id: 'background',
        setup: { posX: offsetX, posY: offsetY, scale: scale }
      });
    }
    
    // For player awards, handle icon settings
    if (cardType === 'player_award') {
      // First, remove any existing icon data
      cardVersion.images = cardVersion.images.filter(img => img.id !== 'icon');
      
      // Then, only add icon data if it should be visible
      if (isIconVisible) {
        cardVersion.images.push({
          id: 'icon',
          setup: { posX: awardIconX, posY: awardIconY, scale: awardIconSize }
        });
      }
    }
    
    // Ensure changes are reflected in the report object
    let existingVersionIndex = report.image_data[cardId].findIndex(v => v.id === cardVersionId);
    if (existingVersionIndex !== -1) {
      report.image_data[cardId][existingVersionIndex] = cardVersion;
    }
    
    // Notify parent of changes
    dispatch('change', report);

    trackEvent('change_image_properties', {
      module: "social_media_generator",
      action_type: "change_data",
      description: 'Change image properties',
    }, sessionInfo?.user?.id);
  }
  
  // Add createEventDispatcher
  const dispatch = createEventDispatcher();
  
  async function preloadImages() {
    try {

      // Preload background image with cache busting
      if (reportImage) {
        try {
          //single background image
          const imageUrl = new URL(reportImage as string, window.location.origin);
          imageUrl.searchParams.set('v', BUILD_TIMESTAMP.toString());
          preloadedImages.background = await loadImagePromise(imageUrl.toString());

          // Extract dominant color from the background image
          if (preloadedImages.background) {
            preloadedImages.dominantColor = extractDominantColor(preloadedImages.background);
          }
        } catch (error) {
          console.warn('Failed to load report image:', error);
          preloadedImages.background = null;
          preloadedImages.dominantColor = null;
        }
      }

      if (reportImages) {
        //multiple background images (eg. for award cards)
        if (cardType === 'player_award') {
          // For player award card, load all award images
          for (const [awardId, imageUrl] of Object.entries(reportImages)) {
            try {
            const url = new URL(imageUrl as string, window.location.origin);
              url.searchParams.set('v', BUILD_TIMESTAMP.toString());
              preloadedImages.backgrounds[awardId] = await loadImagePromise(url.toString());
            } catch (error) {
              console.warn('Failed to load award image:', error);
              preloadedImages.backgrounds[awardId] = null;
            }
          }
        }
      }

      // Preload home team logo with cache busting
      if (!report.opponent_club?.logo_filename) {
        if (report.opponent_club) {
          report.opponent_club.logo_filename = 'default.png';
        }
      }
      if (!sessionInfo?.active_team?.club?.logo_filename) {
        sessionInfo.active_team.club.logo_filename = 'default.png';
      }

      try {
        const homeLogoPath = report.location.toLowerCase() === 'away' 
          ? `/img/clublogos/${report.opponent_club?.logo_filename}?v=${BUILD_TIMESTAMP}`
          : `/img/clublogos/${sessionInfo.active_team.club?.logo_filename}?v=${BUILD_TIMESTAMP}`;
        if (homeLogoPath) {
          const homeLogoUrl = new URL(homeLogoPath, window.location.origin);
          homeLogoUrl.searchParams.set('v', BUILD_TIMESTAMP.toString());
          preloadedImages.homeLogo = await loadImagePromise(homeLogoUrl.toString());
        } 
      } catch (err) {
        console.error('Error preloading home logo:', err);
      }
      try {
        // Preload away team logo with cache busting
        const awayLogoPath = report.location.toLowerCase() === 'away'
          ? `/img/clublogos/${sessionInfo.active_team.club?.logo_filename}?v=${BUILD_TIMESTAMP}`
          : `/img/clublogos/${report.opponent_club?.logo_filename}?v=${BUILD_TIMESTAMP}`;
        if (awayLogoPath) {
          const awayLogoUrl = new URL(awayLogoPath, window.location.origin);
          awayLogoUrl.searchParams.set('v', BUILD_TIMESTAMP.toString());
          preloadedImages.awayLogo = await loadImagePromise(awayLogoUrl.toString());
        }
      } catch (err) {
        console.error('Error preloading away logo:', err);
      }

      imagesLoaded = true;
      if (canvas) {
        drawFrame();
      }
    } catch (err) {
      console.error('Error preloading images:', err);
    }
  }
  
  onMount(async () => {

    await getSessionInfo((result, status) => {
      if (status === 200) {
        sessionInfo = result;
      }
    });

    awards = getAwards();
    for (const award of awards) {
      award.label = $_(`match.awards.${award.id}`);
    }
    selectedAward = awards[0];

    // Load the Tomorrow font with cache busting
    const font = new FontFace(
      'Tomorrow',
      `url(/fonts/tomorrow/Tomorrow-Light.ttf?v=${BUILD_TIMESTAMP})`,
      { weight: '400' }
    );

    const fontBold = new FontFace(
      'Tomorrow',
      `url(/fonts/tomorrow/Tomorrow-Bold.ttf?v=${BUILD_TIMESTAMP})`,
      { weight: '700' }
    );

    // Load Archivo Black font
    const archivoBlack = new FontFace(
      'Archivo Black',
      `url(/fonts/archivo_black/ArchivoBlack-Regular.ttf?v=${BUILD_TIMESTAMP})`,
      { weight: '900' }
    );

    const robotoMedium = new FontFace(
      'Roboto',
      `url(/fonts/roboto/Roboto-Medium.ttf?v=${BUILD_TIMESTAMP})`,
      { weight: '500' }
    );

    try {
      // Force reload fonts by removing them first
      document.fonts.delete(font);
      document.fonts.delete(fontBold);
      document.fonts.delete(archivoBlack);
      document.fonts.delete(robotoMedium);

      await Promise.all([font.load(), fontBold.load(), archivoBlack.load(), robotoMedium.load()]);
      document.fonts.add(font);
      document.fonts.add(fontBold);
      document.fonts.add(archivoBlack);
      document.fonts.add(robotoMedium);
      fontLoaded = true;
    
    } catch (err) {
      console.error('Error loading fonts:', err);
      fontLoaded = true;

    }

    if (canvas) {
      ctx = canvas.getContext('2d');
      canvas.width = frameWidth;
      canvas.height = frameHeight;
            
      // Load weather data in parallel
      if (cardType === 'match_announcement') {
        weatherPromise = await loadWeatherData();
        weatherData = await weatherPromise;
      }
      
      // Start preloading images even if font loading fails
      await preloadImages();

      // Load saved image settings
      loadImageSettings();
      
      // Start the resize button timer
      startResizeButtonTimer();
    }
  });
  
  function drawFrame() {
    if (!ctx || !imagesLoaded) return;
    
    ctx.clearRect(0, 0, frameWidth, frameHeight);
    
    // Draw background image
    if (cardType === 'player_award' && selectedAward && preloadedImages.backgrounds[selectedAward.id]) {
      // For player award, ensure the image covers the left 50% of the canvas
      const awardImage = preloadedImages.backgrounds[selectedAward.id];
      const targetWidth = frameWidth * 0.5; // Left 50% of canvas
      
      // Calculate aspect ratios
      const imgRatio = awardImage.width / awardImage.height;
      const targetRatio = targetWidth / frameHeight;
      
      let drawWidth, drawHeight, x, y;
      
      if (imgRatio > targetRatio) {
        // Image is wider (relative to height) than the target area
        drawHeight = frameHeight;
        drawWidth = drawHeight * imgRatio;
        x = (targetWidth - drawWidth) / 2; // Center within left 50%
        y = 0;
      } else {
        // Image is taller (relative to width) than the target area
        drawWidth = targetWidth;
        drawHeight = drawWidth / imgRatio;
        x = 0;
        y = (frameHeight - drawHeight) / 2;
      }
      
      // Apply manual offset adjustments from user interaction
      x += offsetX;
      y += offsetY;
      
      // Scale from user zoom controls
      drawWidth *= scale;
      drawHeight *= scale;
      
      ctx.drawImage(
        awardImage, 
        x, 
        y, 
        drawWidth, 
        drawHeight
      );
    } else if (preloadedImages.background) {
      // For other cards, calculate image fit based on layout
      const imgRatio = preloadedImages.background.width / preloadedImages.background.height;

      let drawWidth, drawHeight, x, y;

      // Landscape layout: fit image to top 50% of canvas (upper part)
      if (cardType === 'match_report' && layout === 'landscape') {
        const targetHeight = frameHeight * 0.5; // Top 800px for landscape
        const targetRatio = canvas.width / targetHeight;

        if (imgRatio > targetRatio) {
          // Image is wider → fit to height, center horizontally
          drawHeight = targetHeight;
          drawWidth = drawHeight * imgRatio;
          x = (canvas.width - drawWidth) / 2;
          y = 0; // Top-aligned
        } else {
          // Image is taller → fit to width, show upper part
          drawWidth = canvas.width;
          drawHeight = drawWidth / imgRatio;
          x = 0;
          y = 0; // Top-aligned (user can drag down to reveal bottom)
        }
      } else {
        // Portrait and other layouts: ensure background covers the entire canvas
        const canvasRatio = canvas.width / canvas.height;

        if (imgRatio > canvasRatio) {
          // Image is wider than canvas (relative to height)
          drawHeight = canvas.height;
          drawWidth = drawHeight * imgRatio;
          x = (canvas.width - drawWidth) / 2;
          y = 0;
        } else {
          // Image is taller than canvas (relative to width)
          drawWidth = canvas.width;
          drawHeight = drawWidth / imgRatio;
          x = 0;
          y = (canvas.height - drawHeight) / 2;
        }
      }

      // Apply manual offset adjustments from user interaction
      x += offsetX;
      y += offsetY;

      // Scale from user zoom controls
      drawWidth *= scale;
      drawHeight *= scale;

      ctx.drawImage(
        preloadedImages.background,
        x,
        y,
        drawWidth,
        drawHeight
      );
    }
    
    // Draw appropriate overlay based on card type
    if (cardType === 'match_report') {
      // Use layout prop to choose between portrait and landscape
      if (layout === 'landscape') {
        drawMatchReport2();
      } else {
        drawMatchReport();
      }
    } else if (cardType === 'player_award') {
      drawPlayerAward();
    } else {
      drawMatchAnnouncement();
    }
  }
  
  function drawMatchReport() {
    if (!report || !report.match_date) return;
    
    const matchDate = new Date(report.match_date);
    const fontFamily = fontLoaded ? 'Tomorrow' : 'Arial';
    
    const teamY = 345;
    const logoY = 175;
    const logoSize = 130;

    // Create gradient overlay for top section
    drawGradientOverlay(ctx, frameWidth, 600, 0.6, 0);
        
    const { home, away } = getTeamData(report, sessionInfo, report.location.toLowerCase() === 'away');

    // Home team (left side)
    ctx.fillStyle = 'white';
    ctx.font = `bold 52px ${fontFamily}`;
    ctx.textAlign = 'center';
    ctx.fillText(home.name, (frameWidth / 4), 325);

    // Home team logo
    drawLogoScaled(
      ctx,
      preloadedImages.homeLogo,
      getXforRect(frameWidth, 4, 1, 150),
      100,
      150,
      150,
      'LOGO',
      fontFamily
    );

    // VS
    ctx.font = `italic bold 72px ${fontFamily}`;
    ctx.textAlign = 'center';
    ctx.fillText('VS', frameWidth / 2, 225);

    // Away team (right side)
    ctx.font = `bold 52px ${fontFamily}`;
    ctx.fillText(away.name, (frameWidth / 4) * 3, 325);

    // Away team logo
    drawLogoScaled(
      ctx,
      preloadedImages.awayLogo,
      getXforRect(frameWidth, 4, 3, 150),
      100,
      150,
      150,
      'LOGO',
      fontFamily
    );

    // Date information
    ctx.fillStyle = 'white';
    ctx.textAlign = 'center';
    ctx.font = `bold 32px ${fontFamily}`;
    ctx.fillText(`${matchDate.toLocaleDateString('nl-NL', { weekday: 'long' })} ${matchDate.getDate()} ${matchDate.toLocaleDateString('nl-NL', { month: 'long' })}`.toUpperCase(), frameWidth / 2, 380);


    // Score

    // Left score box
    ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
    drawRoundedRect(ctx, (frameWidth / 4) - 85, 410, 170, 170, 35);

    ctx.fillStyle = 'white';
    ctx.font = `bold 95px ${fontFamily}`;
    ctx.textAlign = 'center';
    ctx.fillText(`${home.score}`, (frameWidth / 4), 525);

    // Right score box
    ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
    drawRoundedRect(ctx, ((frameWidth / 4) * 3) - 85, 410, 170, 170, 35);

    ctx.fillStyle = 'white';
    ctx.font = `bold 95px ${fontFamily}`;
    ctx.textAlign = 'center';
    ctx.fillText(`${away.score}`, ((frameWidth / 4) * 3), 525);

    // Draw goal scorers using utility function
    const playerStats = getPlayerStats(report);

    let yOffset = 80;
    let playerHeight = 80;
    const scorerHeight = (playerStats.length * playerHeight) + 50;
    const scorerY = frameHeight - scorerHeight - yOffset - 50;

    drawPlayerStats(ctx, playerStats, {
      x: 50,
      y: scorerY,
      width: 600,
      fontFamily,
      sport: report.team.club.sport,
      columns: 1,
      showTitle: false,
      showBackground: true,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      borderRadius: 35,
      fontSize: 48,
      lineHeight: 80
    });
    
    // Draw PANKLA.COM watermark in lower left
    drawPanklaWatermark(ctx, 60, frameHeight - 50);
  }

  function drawMatchReport2() {
    if (!report || !report.match_date) return;

    const matchDate = new Date(report.match_date);
    const fontFamily = fontLoaded ? 'Tomorrow' : 'Arial';
    const { home, away } = getTeamData(report, sessionInfo, report.location.toLowerCase() === 'away');

    // Get dominant color or fallback to black (needed for both gradients)
    const dominantColor = preloadedImages.dominantColor || { r: 0, g: 0, b: 0 };

    // Top half is reserved for the background image (already drawn in drawFrame)
    // Add horizontal gradient to lower part of image (transparent to dominant color)
    const gradientStartY = frameHeight * 0.25; // Start gradient at 25% of canvas height
    const gradientHeight = frameHeight * 0.25; // Gradient extends to 50% (where bottom section starts)
    const imageGradient = ctx.createLinearGradient(0, gradientStartY, 0, gradientStartY + gradientHeight);
    imageGradient.addColorStop(0, 'rgba(0, 0, 0, 0)');    // Transparent at top
    imageGradient.addColorStop(1, `rgba(${dominantColor.r}, ${dominantColor.g}, ${dominantColor.b}, 1)`);    // Dominant color at bottom
    ctx.fillStyle = imageGradient;
    ctx.fillRect(0, gradientStartY, frameWidth, gradientHeight);

    // Draw large scores over the image on top of gradient
    const logoSpacing = frameWidth / 3;
    const scoreY = frameHeight / 2 - 40; // Position scores just above the bottom section
    ctx.fillStyle = 'white';
    ctx.font = `bold 140px ${fontFamily}`;
    ctx.textAlign = 'center';
    ctx.fillText(`${home.score}`, logoSpacing / 2, scoreY);
    ctx.fillText(`${away.score}`, logoSpacing * 2.5, scoreY);

    // Bottom half - gradient section using dominant color from image
    const bottomSectionY = frameHeight / 2;
    const bottomSectionHeight = frameHeight / 2;

    // Draw background with diagonal gradient for bottom section using dominant color
    const bottomGradient = ctx.createLinearGradient(0, bottomSectionY, frameWidth, frameHeight);
    // Use the darkened dominant color as the starting point
    bottomGradient.addColorStop(0, `rgb(${dominantColor.r}, ${dominantColor.g}, ${dominantColor.b})`);
    // Create an even darker version for the end (multiply by 0.3 for an additional 70% darker)
    const darkerR = Math.round(dominantColor.r * 0.3);
    const darkerG = Math.round(dominantColor.g * 0.3);
    const darkerB = Math.round(dominantColor.b * 0.3);
    bottomGradient.addColorStop(1, `rgb(${darkerR}, ${darkerG}, ${darkerB})`);
    ctx.fillStyle = bottomGradient;
    ctx.fillRect(0, bottomSectionY, frameWidth, bottomSectionHeight);

    // Logo section - horizontal layout (20% smaller: 120 * 0.8 = 96)
    const logoY = bottomSectionY + 60;
    const logoSize = 150;

    // Home team logo (left) - using utility function
    drawLogoScaled(
      ctx,
      preloadedImages.homeLogo,
      logoSpacing / 2 - logoSize / 2,
      logoY,
      logoSize,
      logoSize,
      '',
      fontFamily
    );

    // Date between logos (center)
    ctx.fillStyle = 'white';
    ctx.textAlign = 'center';
    ctx.font = `bold 32px ${fontFamily}`;
    const dayText = `${matchDate.toLocaleDateString('nl-NL', { weekday: 'long' })}`.toUpperCase();
    ctx.fillText(dayText, frameWidth / 2, logoY + logoSize / 2 + 10);
    const dateText = `${matchDate.getDate()} ${matchDate.toLocaleDateString('nl-NL', { month: 'long' })}`.toUpperCase();
    ctx.fillText(dateText, frameWidth / 2, logoY + logoSize / 2 + 60);

    // Away team logo (right) - using utility function
    drawLogoScaled(
      ctx,
      preloadedImages.awayLogo,
      logoSpacing * 2.5 - logoSize / 2,
      logoY,
      logoSize,
      logoSize,
      '',
      fontFamily
    );

    // Team names below logos
    ctx.fillStyle = 'white';
    ctx.font = `bold 28px ${fontFamily}`;
    ctx.textAlign = 'center';

    // Home team name
    const homeTeamY = logoY + logoSize + 40;
    drawAutoSizedText(ctx, home.name, logoSpacing / 2, homeTeamY, logoSpacing - 40, 28, 22, fontFamily, 1, 'bold');

    // Away team name
    drawAutoSizedText(ctx, away.name, logoSpacing * 2.5, homeTeamY, logoSpacing - 40, 28, 22, fontFamily, 1, 'bold');

    // Draw goal scorers section - two column layout (moved up with more space)
    const playerStats = getPlayerStats(report);
    const scorersStartY = homeTeamY + 60; // Start right after team names

    drawPlayerStats(ctx, playerStats, {
      x: 60,
      y: scorersStartY,
      width: frameWidth - 120,
      fontFamily,
      sport: report.team.club.sport,
      columns: 2,
      showTitle: false,
      showBackground: false,
      fontSize: 42,
      lineHeight: 52,
      maxPlayersPerColumn: 7
    });



    drawPanklaWatermark(ctx, 60, frameHeight - 50);

  }

  async function drawMatchAnnouncement() {
    if (!report || !report.match_date) return;

    const matchDate = new Date(report.match_date);

    const titleFont = fontLoaded ? 'Tomorrow' : 'Arial';
    const headerFont = fontLoaded ? 'Tomorrow' : 'Arial';
    const textFont = fontLoaded ? 'Roboto' : 'Arial';
    const smallText = "32px";
    const mediumText = "48px";
    const largeText = "56px";
  
    const { home, away } = getTeamData(report, sessionInfo,report.location.toLowerCase() === 'away');
    const clubY = 125;
    const teamY = 345;
    const logoY = 150;
    const logoSize = 135;

    // Create gradient overlay for top section
    const gradient = ctx.createLinearGradient(0, 0, 0, 1000);
    gradient.addColorStop(0, 'rgba(0, 0, 0, 0.6)');    // 60% black at top
    gradient.addColorStop(0.7, 'rgba(0, 0, 0, 0.4)');  // 40% black at 20%
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');      // Fully transparent at bottom
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, frameWidth, 1000);
    
    
    // Home team (left side)
    ctx.fillStyle = 'white';
    ctx.font = `bold ${mediumText} ${titleFont}`;
    ctx.textAlign = 'center';
    ctx.fillText(home.name, (frameWidth / 4), teamY);

    // Home team logo
    if (preloadedImages.homeLogo) {
      const x = getXforRect(frameWidth, 4, 1, logoSize);
      const y = logoY;
      const width = logoSize;
      const height = logoSize;
      
      const scale = Math.min(width / preloadedImages.homeLogo.width, height / preloadedImages.homeLogo.height);
      const scaledWidth = preloadedImages.homeLogo.width * scale;
      const scaledHeight = preloadedImages.homeLogo.height * scale;
      
      const xOffset = (width - scaledWidth) / 2;
      const yOffset = (height - scaledHeight) / 2;
      
      ctx.drawImage(
        preloadedImages.homeLogo,
        x + xOffset,
        y + yOffset,
        scaledWidth,
        scaledHeight
      );
      ctx.font = `normal 24px ${titleFont}`;
      ctx.textAlign = 'center';
      ctx.fillText(home.club, (frameWidth / 4), clubY);

    } else {
      ctx.strokeStyle = 'white';
      ctx.strokeRect(getXforRect(frameWidth, 4, 1, logoSize), logoY, logoSize, logoSize);
      ctx.font = `bold 24px ${titleFont}`;
      ctx.textAlign = 'center';
      ctx.fillText('LOGO', frameWidth / 4, logoY+35);
    }
    
    // VS
    ctx.font = `italic bold ${largeText} ${titleFont}`;
    ctx.textAlign = 'center';
    ctx.fillText('VS', frameWidth / 2, logoY+100);

    // Away team (right side)
    ctx.font = `bold ${mediumText} ${titleFont}`;
    ctx.textAlign = 'center';
    ctx.fillText(away.name, (frameWidth / 4) *3, teamY);

    // Away team logo
    if (preloadedImages.awayLogo) {
      const x = getXforRect(frameWidth, 4, 3, logoSize);
      const y = logoY;
      const width = logoSize;
      const height = logoSize;
      
      const scale = Math.min(width / preloadedImages.awayLogo.width, height / preloadedImages.awayLogo.height);
      const scaledWidth = preloadedImages.awayLogo.width * scale;
      const scaledHeight = preloadedImages.awayLogo.height * scale;
      
      const xOffset = (width - scaledWidth) / 2;
      const yOffset = (height - scaledHeight) / 2;
      
      ctx.drawImage(
        preloadedImages.awayLogo,
        x + xOffset,
        y + yOffset,
        scaledWidth,
        scaledHeight
      );
      ctx.font = `normal 24px ${titleFont}`;
      ctx.textAlign = 'center';
      ctx.fillText(away.club, (frameWidth / 4) * 3, clubY);
    } else {
      ctx.strokeStyle = 'white';
      ctx.strokeRect(getXforRect(frameWidth, 4, 3, logoSize), logoY, logoSize, logoSize);
      ctx.font = `bold 24px ${titleFont}`;
      ctx.textAlign = 'center';
      ctx.fillText('LOGO', (frameWidth / 4) * 3, logoY+35);
    }

    // Date and time information
    const dateY = 400;
    ctx.fillStyle = 'white';
    ctx.textAlign = 'center';
    ctx.font = `bold ${smallText} ${headerFont}`;
    ctx.fillText(formatMatchDate(matchDate).toUpperCase() , frameWidth / 2, dateY);
    const time = report.match_date.split('T')[1].slice(0, 5); // Gets HH:MM from the datetime string
    ctx.fillStyle = 'white';
    ctx.textAlign = 'center';
    ctx.font = `bold ${largeText} ${headerFont}`;
    ctx.fillText(time, frameWidth / 2, dateY+60);

    // Location with auto-sizing
    let locationY = 520;
    const maxLocationWidth = frameWidth - 100; // Leave some margin
    
    if (report.location == 'home' && sessionInfo.active_team.club.location_name) {
      // Draw location name with auto-sizing
      locationY = drawAutoSizedText(
        ctx, 
        sessionInfo.active_team.club.location_name, 
        frameWidth / 2, 
        locationY, 
        maxLocationWidth, 
        56, // initial font size
        45, // minimum font size
        headerFont, 
        2, // max lines
        'bold' // font weight
      );
      
      // Draw address with auto-sizing
      const address = `${sessionInfo.active_team.club?.location_address || ''} ${sessionInfo.active_team.club?.location_city || ''}`.trim();
      if (address) {
        drawAutoSizedText(
          ctx, 
          address, 
          frameWidth / 2, 
          locationY + 20, 
          maxLocationWidth, 
          34, // initial font size
          34, // minimum font size
          headerFont, 
          2, // max lines
          'normal' // font weight
        );
      }
    } else if (report.location == 'away' && report.opponent_club?.location_name) {
      // Draw location name with auto-sizing
      locationY = drawAutoSizedText(
        ctx, 
        report.opponent_club?.location_name || '', 
        frameWidth / 2, 
        locationY, 
        maxLocationWidth, 
        56, // initial font size
        45, // minimum font size
        headerFont, 
        2, // max lines
        'bold' // font weight
      );
      
      // Draw address with auto-sizing
      const address = `${report.opponent_club?.location_address || ''} ${report.opponent_club?.location_city || ''}`.trim();
      if (address) {
        drawAutoSizedText(
          ctx, 
          address, 
          frameWidth / 2, 
          locationY + 20, 
          maxLocationWidth, 
          34, // initial font size
          34, // minimum font size
          headerFont, 
          2, // max lines
          'normal' // font weight
        );
      }
    } else {
      drawAutoSizedText(
        ctx, 
        report.location, 
        frameWidth / 2, 
        locationY + 20, 
        maxLocationWidth, 
        56, // initial font size
        45, // minimum font size
        headerFont, 
        1, // max lines
        'bold' // font weight
      );
    }

    // Match info box (bottom)
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    
    // Modern browsers support roundRect
    let taskHeight = getTaskHeight((report.task_list || []).length);
    let taskYOffset = 425;
    if (ctx.roundRect) {
      ctx.roundRect(
        25, 
        frameHeight - taskHeight - taskYOffset, 
        frameWidth - 50, 
        taskHeight + 75,
        25  // radius of the rounded corners in pixels
      );
      ctx.fill();
    } else {
      // Fallback for older browsers
      const x = 25;
      const y = frameHeight - taskHeight - taskYOffset;
      const width = frameWidth - 50;
      const height = taskHeight + 75;
      const radius = 35;

      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.arcTo(x + width, y, x + width, y + height, radius);
      ctx.arcTo(x + width, y + height, x, y + height, radius);
      ctx.arcTo(x, y + height, x, y, radius);
      ctx.arcTo(x, y, x + width, y, radius);
      ctx.closePath();
      ctx.fill();
    }

    //Styles for detail box
    ctx.fillStyle = 'white';
    ctx.font = `bold ${smallText} ${textFont}`;
    ctx.textAlign = 'left';

    //Meeting time
    const meetingIcon = '⏰';
    const meetingText = `${report.meeting_time || ""}  ${report.meeting_location || ""}`;
    const maxWidth = frameWidth -120;
    const lineHeight = 40;
    const startY = frameHeight - taskHeight - taskYOffset + 75;
    ctx.fillText(meetingIcon, 55, startY);
    wrapText(ctx, meetingText, 110, startY, maxWidth, lineHeight);


    // Weather info (top right)
    // Use the pre-loaded weather data instead of fetching it again
  if (weatherData) {
          // Find forecast for match date
      const forecast = weatherData.forecast.forecastday
        .find(day => day.date === matchDate.toISOString().split('T')[0])
        ?.hour
        .find(hour => {
          const matchDateRounded = roundToNearestHour(matchDate);
          const searchDateTime = `${matchDateRounded.getFullYear()}-${String(matchDateRounded.getMonth() + 1).padStart(2, '0')}-${String(matchDateRounded.getDate()).padStart(2, '0')} ${String(matchDateRounded.getHours()).padStart(2, '0')}:00`;
          return hour.time === searchDateTime;
        });
        
      if (forecast) {
        // Get weather icon and max temp for the day
        const icon = forecast.condition.text;
        const temp = Math.round(forecast.temp_c);
        
        // Map weather condition to emoji
        const weatherEmoji = getWeatherEmoji(icon);
        const weatherText = `${temp}°`;
        const maxWidth = frameWidth -120;
        const lineHeight = 50;
        const startY = frameHeight - taskHeight - taskYOffset + 75;
        ctx.fillText(weatherEmoji, 55, startY + 50);
        wrapText(ctx, weatherText, 110, startY + 50, maxWidth, lineHeight);
      }
    }

    // Filter task list to only show specific roles
    //const importantRoles = ['referee', 'linesman', 'fruit', 'keeper', 'coaching', 'driving'];
    let filteredTasks = report.task_list || [];
    // filteredTasks = filteredTasks.filter(task => 
    //   importantRoles.includes(task.task_id)
    // );

    // Display tasks from report.task_list
    if (filteredTasks.length > 0) {
      const startY = frameHeight - taskHeight - taskYOffset + 180;
      const lineHeight = 50; // Space between lines
      
      filteredTasks.forEach((task, index) => {
        const task_icon = task.icon ? task.icon : '';
        const fullText = `${task.label}: ${task.user_name}`;
        const trimmedText = fullText.substring(0, 55);
        const y = startY + (index * lineHeight);
        ctx.fillText(task_icon, 50, y);
        wrapText(ctx, trimmedText, 110, y, maxWidth, lineHeight);
      });
    }

    // Draw PANKLA.COM watermark in lower left
    drawPanklaWatermark(ctx, 60, frameHeight - 50);
  }
  
  function drawPlayerAward() {
    if (!report || !selectedAward || !report.match_date) return;

    const award = (report.player_awards || []).find(award => award.id === selectedAward.id);
    if (!award) return;
    
    award.label = $_(`match.awards.${award.id}`);
    const awardIcon = selectedAward?.icon || '🏆';

    const matchDate = new Date(report.match_date);
    const fontFamily = fontLoaded ? 'Tomorrow' : 'Arial';
    
    // Draw diagonal split background
    // Left side is the background image (already drawn in drawFrame)
    // Right side is a blue background
    ctx.fillStyle = '#1e3a8a'; // Dark blue
    
    // Draw a polygon to create the right side color block
    ctx.beginPath();
    ctx.moveTo(frameWidth * 0.5, 0); // Start at 40% from left, top
    ctx.lineTo(frameWidth, 0); // Top right corner
    ctx.lineTo(frameWidth, frameHeight); // Bottom right corner
    ctx.lineTo(frameWidth * 0.35, frameHeight); // 30% from left, bottom
    ctx.closePath();
    ctx.fill();
    
    // Draw team logo at the top center
    if (preloadedImages.homeLogo) {
      const logoSize = 130;
      const x = (frameWidth / 2) - (logoSize / 2);
      const y = 30;
      
      ctx.drawImage(
        preloadedImages.homeLogo,
        x, y, logoSize, logoSize
      );
    }
    
    // Format date (day number + month name)
    const dateText = `${matchDate.getDate()} ${matchDate.toLocaleDateString('nl-NL', { month: 'long' })}`;
    
    // Draw date text
    ctx.fillStyle = 'white';
    ctx.font = `bold 32px ${fontFamily}`;
    ctx.textAlign = 'left';
    ctx.fillText(dateText, frameWidth * 0.7, 100);
    
    // Draw "AWARD" text
    ctx.font = `bold 42px gagalin`;
    
    // Split the award label into two parts if needed
    if (award.label && award.label.includes(" ")) {
      const parts = award.label.split(" ");
      const midpoint = Math.floor(parts.length / 2);
      
      // First line (first half of words)
      const firstLine = parts.slice(0, midpoint).join(" ");
      ctx.fillText(firstLine, frameWidth * 0.5, 230);
      
      // Second line (second half of words)
      const secondLine = parts.slice(midpoint).join(" ");
      ctx.fillText(secondLine, frameWidth * 0.5, 280);
    } else {
      // If no spaces, just show on one line
      ctx.fillText(award.label, frameWidth * 0.5, 230);
    }
    
    ctx.fillStyle = 'white';
    ctx.font = `bold 42px gagalin`;
    ctx.fillText(award.name, frameWidth * 0.5, 380);
        
    // Award reason section title
    ctx.font = `bold italic 32px ${fontFamily}`;
    ctx.fillText(`${$_('socialMediaGenerator.awardReason')}`, frameWidth * 0.5, 480);
    
    // Split comments into bullet points
    const comments = award.comments?.split('\n') || [];
    ctx.font = `normal 24px ${fontFamily}`;
    
    // Define max width for comment text to prevent overflow
    const maxCommentWidth = frameWidth * 0.45; // Limit text width to 45% of frame width
    const lineHeight = 40; // Space between lines
    
    let yPosition = 530; // Starting Y position for comments
    
    comments.forEach((comment, index) => {
      if (comment.trim()) {
        // Use wrapText utility to properly wrap long comments
        yPosition = wrapText(ctx, `• ${comment.trim()}`, frameWidth * 0.5, yPosition, maxCommentWidth, lineHeight);
      }
    });
    
    // Draw award emoji/icon (crown) only if icon is visible
    if (isIconVisible) {
      ctx.font = `normal ${awardIconSize}px Arial`;
      ctx.fillStyle = 'white';
      ctx.fillText(awardIcon, awardIconX, awardIconY);
    }
    
    // Draw PANKLA.COM watermark in lower left
    drawPanklaWatermark(ctx, 60, frameHeight - 50);
  }
  
  async function shareOrDownloadImage() {
    // Hide the resize button before sharing to prevent it from being included in the image
    hideResizeButton();
    
    try {
      // First check basic share capability
      if (!navigator.share) {
        console.log('Web Share API not supported');
        return downloadImage();
      }

      // Convert canvas to blob for sharing
      const blob = await new Promise<Blob>(resolve => canvas.toBlob(resolve, 'image/png'));
      
      // Create a file object from the blob
      const file = new File([blob], `${sessionInfo.active_team.name}-vs-${report.opponent_club?.name}.png`, { type: 'image/png' });
      const { home, away } = getTeamData(report, sessionInfo,report.location.toLowerCase() === 'away');
      
      let text = '';

      // Try sharing with files first (preferred)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            title: `${home.name} vs ${away.name}`,
            text: text,
            files: [file]
          });
          trackEvent('share_image', {
            module: "social_media_generator",
            action_type: "share_data",
            description: 'Share image',
            value: cardType,
            variant: 'file'
          }, sessionInfo?.user?.id);
          // Track share to backend
          trackShareToBackend();
          return;
        } catch (fileErr) {
          console.warn('File sharing failed, trying URL share', fileErr);
          // Continue to URL sharing fallback
        }
      }
      
      // Fallback: Try sharing as a data URL
      try {
        const imageUrl = canvas.toDataURL('image/png');
        await navigator.share({
          title: `${home.name} vs ${away.name}`,
          text: '',
          url: imageUrl // Note: this might not work on all browsers
        });
        trackEvent('share_image', {
          module: "social_media_generator",
          action_type: "share_data",
          description: 'Share image',
          value: cardType,
          variant: 'url'
        }, sessionInfo?.user?.id);
        // Track share to backend
        trackShareToBackend();
        console.log('Image shared successfully with URL');
      } catch (urlErr) {
        console.warn('URL sharing failed, trying alternate approach', urlErr);
        
        // Alternative approach: Create a temporary URL in browser memory
        const blobUrl = URL.createObjectURL(blob);
        try {
          trackEvent('share_image', {
            module: "social_media_generator",
            action_type: "share_data",
            description: 'Share image',
            value: cardType,
            variant: 'blob_url'
          }, sessionInfo?.user?.id);
          await navigator.share({
            title: `${home.name} vs ${away.name}`,
            text: 'Check out this match result!',
            url: blobUrl
          });
          // Track share to backend
          trackShareToBackend();
          // Clean up the URL after sharing
          setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
          console.log('Image shared successfully with blob URL');
        } catch (blobErr) {
          console.error('All sharing attempts failed', blobErr);
          downloadImage();
        }
      }
    } catch (err) {
      console.error('Error in share function:', err);
      downloadImage();
    }
  }
  
  function downloadImage() {
    const link = document.createElement('a');
    link.download = `${sessionInfo.active_team.name}-vs-${report.opponent_club?.name}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    // Track download as a share
    trackShareToBackend();
  }

  function trackShareToBackend() {
    if (!report || !report.id) return;

    // Determine share type based on cardType
    const shareType = cardType === 'match_announcement' ? 'announcement' : 'report';

    // Call API to track share
    trackMatchShare(report.id, shareType, (result, status) => {
      if (status === 200) {
        console.log('Share tracked successfully', result);
      } else {
        console.warn('Failed to track share', status);
      }
    });
  }
  
  async function openEditor() {
    // Hide the resize button when editor opens
    hideResizeButton();
    
    // Scroll to top and wait for it to complete
    await new Promise(resolve => {
      window.scrollTo({ top: 0, behavior: 'instant' });
      // Small delay to ensure scroll completes
      setTimeout(resolve, 50);
    });
    
    isEditorOpen = true;
    // We need to ensure the image is drawn on the fullscreen canvas after it's opened
    setTimeout(() => {
      if (canvasFullscreen) {
        // Transfer context to the fullscreen canvas
        ctx = canvasFullscreen.getContext('2d');
        
        // Keep original dimensions
        canvasFullscreen.width = frameWidth;
        canvasFullscreen.height = frameHeight;
        canvasFullscreen.style.backgroundColor = 'black';
        
        drawFrame();
      }
    }, 50); // Small delay to ensure the canvas is in the DOM
  }
  
  function closeEditor() {
    isEditorOpen = false;
    // Dispatch an event to notify parent component that image data has changed
    saveImageSettings();
    dispatch('change', report);
    // Switch context back to the original canvas
    if (canvas) {
      ctx = canvas.getContext('2d');
      drawFrame();
    }
  }
  
  function zoomIn() {
    if (moveMode === 'icon' && cardType === 'player_award') {
      // Scale icon when in icon mode
      awardIconSize *= 1.1;
      // Don't let icon get too small
      awardIconSize = Math.min(500, awardIconSize);
    } else {
      // Scale background in normal mode
      scale *= 1.1;
    }
    drawFrame();
  }
  
  function zoomOut() {
    if (moveMode === 'icon' && cardType === 'player_award') {
      // Scale icon when in icon mode
      awardIconSize *= 0.9;
      // Don't let icon get too small
      awardIconSize = Math.max(20, awardIconSize);
    } else {
      // Scale background in normal mode
      scale *= 0.9;
    }
    drawFrame();
  }
  
  function startDrag(e) {
    if (!isEditorOpen) return;
    
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
    const clientY = e.clientY || (e.touches && e.touches[0]?.clientY);
    
    if (!clientX || !clientY) return;
    
    // Get initial positions for both background and icon dragging
    isDragging = true;
    lastX = clientX;
    lastY = clientY;
    
    // Prevent default to stop page scrolling
    e.preventDefault();
  }
  
  function drag(e) {
    if (!isDragging) return;
    
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
    const clientY = e.clientY || (e.touches && e.touches[0]?.clientY);
    
    if (!clientX || !clientY) return;
    
    const deltaX = clientX - lastX;
    const deltaY = clientY - lastY;
    
    if (moveMode === 'icon' && cardType === 'player_award') {
      // Move the icon when in move icon mode
      const canvasRect = canvasFullscreen.getBoundingClientRect();
      
      // Scale the movement based on canvas scaling
      const scaleX = frameWidth / canvasRect.width;
      const scaleY = frameHeight / canvasRect.height;
      
      // Apply the movement directly to icon position
      awardIconX += deltaX * scaleX;
      awardIconY += deltaY * scaleY;
      
      // Optional: Add boundary checks to keep icon on canvas
      awardIconX = Math.max(20, Math.min(frameWidth - 20, awardIconX));
      awardIconY = Math.max(20, Math.min(frameHeight - 20, awardIconY));
      
    } else {
      // Move the background image in normal mode
      offsetX += deltaX;
      offsetY += deltaY;
    }
    
    lastX = clientX;
    lastY = clientY;
    
    drawFrame();
    
    // Prevent default to stop page scrolling
    e.preventDefault();
  }
  
  function endDrag(e) {
    if (isDragging) {
      isDragging = false;
    }
  }
  
  // Update award selection to load settings when changing awards
  $: if (selectedAward && cardType === 'player_award') {
    loadImageSettings();
  }
  
  // Update the icon state when visibility changes
  $: if (cardType === 'player_award') {
    if (!isIconVisible) {
      moveMode = 'background';
    }
    if (canvas) {
      drawFrame();
    }
  }

  // Reload settings and redraw when layout changes for match reports
  $: if (layout && canvas && cardType === 'match_report') {
    loadImageSettings();
    drawFrame();
  }
</script>

<div>
  {#if cardType === 'player_award'}
    <div class="controls-container">
      <select bind:value={selectedAward} on:change={() => {
        if (canvas) drawFrame();
      }}>
        {#each awards || [] as award}
          <option value={award}>{award.icon} {award.label}</option>
        {/each}
      </select>
    </div>
  {/if}
</div>

<div class="social-media-generator">
  {#if cardType === 'player_award' && !hasPlayerForAward}
    <div class="no-player-message">
      <p>{$_('socialMediaGenerator.noPlayerSelected')}</p>
    </div>
  {:else}
    <div class="image-container" on:click={openEditor}>
      <canvas
        bind:this={canvas}
        width={frameWidth}
        height={frameHeight}
      ></canvas>
    </div>
  {/if}
</div>

{#if showResizeButton && !(cardType === 'player_award' && !hasPlayerForAward)}
  <button class="resize-hint-button" on:click={openEditor}>
    <ion-icon name="resize-outline"></ion-icon>
    {$_('socialMediaGenerator.clickToResize')}
  </button>
{/if}

{#if !(cardType === 'player_award' && !hasPlayerForAward)}
  <div class="sticky-actions flex flex-col items-center">
    {#if cardType === 'match_announcement'}
      <p class="text text-sm text-italic"><ion-icon class="text text-primary" name="warning-outline"></ion-icon> {$_('shareView.warning')}</p>
    {/if}
    <button class="success full-width share-button" on:click={shareOrDownloadImage}>
      <ion-icon name="share-outline"></ion-icon>
      {isShareAvailable()
        ? $_('socialMediaGenerator.share')
        : $_('socialMediaGenerator.download')}
    </button>
  </div>
{/if}

{#if isEditorOpen}
  <div class="fullscreen-editor">
    <div class="editor-content">
      <button class="close-button" on:click={closeEditor}>×</button>
      <canvas 
        bind:this={canvasFullscreen}
        width={frameWidth} 
        height={frameHeight}
        on:mousedown={startDrag}
        on:mousemove={drag}
        on:mouseup={endDrag}
        on:mouseleave={endDrag}
        on:touchstart={startDrag}
        on:touchmove={drag}
        on:touchend={endDrag}
      ></canvas>
      <!-- <p class="text text-sm">{$_('socialMediaGenerator.editor.explanation')}</p> -->
      <div class="editor-controls bottom">
        {#if cardType === 'player_award'}
        <!-- Disable icon temporarily-->
          <!-- Add icon visibility toggle button -->
          <!-- <button 
            class={isIconVisible ? "primary icon-button small mb-0" : "secondary icon-button small outline mb-0"} 
            on:click={() => {
              isIconVisible = !isIconVisible;
              // If hiding icon, ensure we're in background mode
              if (!isIconVisible) {
                moveMode = 'background';
              }
              drawFrame();
            }}
          >
            {#if selectedAward?.icon}
              {selectedAward.icon}
            {:else}
              🏆
            {/if}
          </button>
           -->
          <!-- Only show dropdown when icon is visible -->
          <!-- {#if isIconVisible}
            <select bind:value={moveMode} class="secondary small mb-0">
              <option value="background">{$_('socialMediaGenerator.editor.moveBackground')}</option>
              <option value="icon">{$_('socialMediaGenerator.editor.moveIcon')}</option>
            </select>
          {/if} -->
        {/if}
        <button class="secondary small flex items-center" on:click={zoomIn} on:touchend|preventDefault={zoomIn}><ion-icon name="search"></ion-icon>+</button>
        <button class="secondary small flex items-center" on:click={zoomOut} on:touchend|preventDefault={zoomOut}><ion-icon name="search"></ion-icon>-</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .social-media-generator {
    margin-bottom: 80px; /* Space for the sticky button */
  }

  .no-player-message {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    background: var(--pico-card-background-color);
    border: 1px solid var(--pico-border-color);
    border-radius: var(--pico-border-radius);
    padding: 2rem;
  }

  .no-player-message p {
    color: var(--pico-muted-color);
    font-size: 1.1rem;
    text-align: center;
    margin: 0;
  }

  .image-container {
    position: relative;
    max-width: 100%;
    overflow: hidden;
    border: 1px solid transparent;
    cursor: pointer;
    background: #000000
  }
  
  .image-container canvas {
    max-width: 100%;
    height: auto;
  }
  
  @keyframes wiggle {
    0%, 100% { transform: translateX(-50%) translateX(0px); }
    25% { transform: translateX(-50%) translateX(-3px); }
    75% { transform: translateX(-50%) translateX(3px); }
  }

  .resize-hint-button {
    position: fixed;
    bottom: 130px; /* Just above the sticky header which is ~80px high + some margin */
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.8);
    color: white;
    border: none;
    padding: 12px 12px;
    border-radius: 10px;
    border: 1px solid white;
    font-size: 0.9rem;
    cursor: pointer;
    z-index: 998; /* Just below sticky actions (999) but above other content */
    backdrop-filter: blur(4px);
    transition: opacity 0.1s ease;
    opacity: 0.85;
    animation: wiggle 0.1s ease-in-out 3; /* Wiggle 5 times quickly */
    animation-delay: 1s; /* Wait 1 second before starting the wiggle */
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .resize-hint-button:hover {
    background: rgba(0, 0, 0, 0.9);
  }
  
  .sticky-actions {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 1rem;
    background: var(--pico-background-color);
    border-top: 1px solid var(--pico-border-color);
    display: flex;
    justify-content: center;
    z-index: 999;
  }

  
  .share-button {
    justify-content: center;
  }

  .editor-controls {
    display: flex;
    gap: 0.8rem;
    padding: 12px;
    margin-bottom: 10px;
    justify-content: center;
    width: 100%;
    background: transparent;
    position: fixed;
    z-index: 1001;
    touch-action: auto;
  }
  
  .editor-controls.bottom {
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.7);
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    margin-bottom: 0;
    padding: 15px;
  }
  
  .fullscreen-editor {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.9);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1010;
    /* Prevent scrolling when editor is open */
    overflow: hidden;
    touch-action: none;
  }
  
  .editor-content {
    position: relative;
    background: transparent;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding-bottom: 70px; /* Space for controls at bottom */
  }
  
  .editor-content canvas {
    display: block;
    background: #fff;
    width: auto;
    height: calc(100vh - 100px); /* Subtract space for controls */
    max-width: 100%;
    max-height: calc(100vh - 100px);
    object-fit: contain;
    cursor: move;
    /* Prevent text selection while dragging */
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }
  
  .controls-container {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    align-items: center;
  }

  
  .close-button {
    position: absolute;
    top: 10px;
    right: 10px;
    background: rgba(0, 0, 0, 0.5);
    color: white;
    border: none;
    font-size: 2rem;
    line-height: 1;
    padding: 0.5rem;
    cursor: pointer;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    border-radius: 50%;
    z-index: 1002;
  }

  .close-button:hover {
    cursor: pointer;
  }
  
</style> 